/**
 * OrganizrContextStorage.ts - Store fixed navigation contexts in Organizr
 * 
 * CHANGE HISTORY:
 * - 251122_1515 danielbeer: Created for Phase 3 integration
 */

import type { NavigationContext } from '../components/ContextSelector';

export class OrganizrContextStorage {
  private organizr: any;
  private listId = 'navigation-contexts';

  constructor(organizr?: any) {
    this.organizr = organizr;
  }

  /**
   * Initialize context storage in Organizr
   */
  async initialize(): Promise<void> {
    if (!this.organizr) {
      const { getOrganizr } = await import('@opcentral/organizr');
      this.organizr = getOrganizr();
    }

    // Create list if it doesn't exist
    try {
      this.organizr.getList(this.listId);
    } catch (e) {
      this.organizr.createList(this.listId, {
        name: 'Navigation Contexts',
        description: 'System navigation contexts',
        itemType: 'navigation-context'
      });
    }
  }

  /**
   * Get all fixed contexts
   */
  async getFixedContexts(): Promise<NavigationContext[]> {
    await this.initialize();
    
    try {
      const list = this.organizr.getList(this.listId);
      const items = list.getItems();
      return items.filter((item: any) => item.type === 'fixed');
    } catch (e) {
      console.warn('[OrganizrContextStorage] Failed to get contexts:', e);
      return [];
    }
  }

  /**
   * Add a fixed context
   */
  async addFixedContext(context: NavigationContext): Promise<void> {
    await this.initialize();
    
    const list = this.organizr.getList(this.listId);
    list.addItem({
      ...context,
      type: 'fixed'
    });
  }

  /**
   * Update a fixed context
   */
  async updateFixedContext(contextId: string, updates: Partial<NavigationContext>): Promise<void> {
    await this.initialize();
    
    const list = this.organizr.getList(this.listId);
    list.updateItem(contextId, updates);
  }

  /**
   * Delete a fixed context
   */
  async deleteFixedContext(contextId: string): Promise<void> {
    await this.initialize();
    
    const list = this.organizr.getList(this.listId);
    list.removeItem(contextId);
  }

  /**
   * Get a specific context by ID
   */
  async getContext(contextId: string): Promise<NavigationContext | null> {
    await this.initialize();
    
    try {
      const list = this.organizr.getList(this.listId);
      return list.getItem(contextId);
    } catch (e) {
      return null;
    }
  }

  /**
   * Seed default contexts
   */
  async seedDefaultContexts(): Promise<void> {
    const existing = await this.getFixedContexts();
    if (existing.length > 0) return;

    // Add default FMwvTool contexts
    await this.addFixedContext({
      id: 'fmwvtool-all',
      name: 'All Tools',
      description: 'All available FMwvTool plugins',
      type: 'fixed',
      icon: '🔧',
      items: [
        { id: 'home', label: 'Home', icon: '🏠', category: 'System' },
        { id: 'outliner', label: 'Outliner', icon: '📋', category: 'Organization' },
        { id: 'picker', label: 'Picker', icon: '🎯', category: 'UI' },
        { id: 'tag-manager', label: 'Tag Manager', icon: '🏷️', category: 'Organization' },
        { id: 'pdf-viewer', label: 'PDF Viewer', icon: '📄', category: 'Documents' },
        { id: 'json-viewer', label: 'JSON Viewer', icon: '{ }', category: 'Data' }
      ]
    });

    await this.addFixedContext({
      id: 'fmwvtool-documents',
      name: 'Document Tools',
      description: 'Tools for document processing',
      type: 'fixed',
      icon: '📄',
      items: [
        { id: 'pdf-viewer', label: 'PDF Viewer', icon: '📄', category: 'Documents' },
        { id: 'json-viewer', label: 'JSON Viewer', icon: '{ }', category: 'Documents' }
      ]
    });

    await this.addFixedContext({
      id: 'fmwvtool-data',
      name: 'Data Tools',
      description: 'Tools for data processing',
      type: 'fixed',
      icon: '🔄',
      items: [
        { id: 'field-matcher', label: 'Field Matcher', icon: '🔗', category: 'Data' },
        { id: 'table-viewer', label: 'Table Viewer', icon: '📊', category: 'Data' },
        { id: 'json-viewer', label: 'JSON Viewer', icon: '{ }', category: 'Data' }
      ]
    });
  }
}

// Singleton
let storageInstance: OrganizrContextStorage | null = null;

export function getOrganizrContextStorage(): OrganizrContextStorage {
  if (!storageInstance) {
    storageInstance = new OrganizrContextStorage();
  }
  return storageInstance;
}
